import { useEffect, useState } from "react";
import { toast } from "sonner";

/**
 * useSetlistLock — extracts the SetList + SetList-Lock state machine
 * from RadioHostStudio so the page component can stay focussed on
 * presentation.
 *
 * Responsibilities:
 *   • Persist the queue + lock flag per-slug in localStorage.
 *   • Expose enqueue / dequeue / move / clear helpers.
 *   • When the lock is armed AND auto-mix completes a sweep
 *     (`sweepCompletedAt` bumps), load the next queued track onto the
 *     FROM deck (the one that just faded out) using the provided
 *     `smartLoad` adapter.
 *   • When the lock is first armed (or either deck becomes empty),
 *     auto-hydrate the empty deck(s) from the head of the queue so the
 *     host doesn't have to "load A, load B, hit play" manually.
 *
 * The hook is intentionally deck-agnostic — callers inject
 * `{ deckA, setDeckA, setPlayA, deckB, setDeckB, setPlayB, smartLoad }`
 * plus the sweep coordinates (`sweepCompletedAt`, `sweepFromRef`).
 *
 * @param {Object} opts
 * @param {string} opts.slug            Studio slug, used for per-show persistence keys.
 * @param {any}    opts.deckA           Current track loaded on deck A (falsy = empty).
 * @param {any}    opts.deckB           Current track loaded on deck B (falsy = empty).
 * @param {Fn}     opts.setDeckA
 * @param {Fn}     opts.setDeckB
 * @param {Fn}     opts.setPlayA
 * @param {Fn}     opts.setPlayB
 * @param {Fn}     opts.smartLoad       (track, setDeck, setPlay) => void
 * @param {number} opts.sweepCompletedAt Monotonic counter bumped on each sweep end.
 * @param {Object} opts.sweepFromRef    Ref whose `.current` is "A"|"B" for the fading-out deck.
 */
export default function useSetlistLock({
  slug,
  deckA,
  deckB,
  setDeckA,
  setDeckB,
  setPlayA,
  setPlayB,
  smartLoad,
  sweepCompletedAt,
  sweepFromRef,
}) {
  const SETLIST_KEY = `tapin_dj_setlist_${slug || "global"}`;
  const SETLIST_LOCK_KEY = `tapin_dj_setlist_lock_${slug || "global"}`;

  const [setlist, setSetlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SETLIST_KEY) || "[]"); } catch { return []; }
  });
  const [setlistLock, setSetlistLock] = useState(() => {
    try { return localStorage.getItem(SETLIST_LOCK_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(SETLIST_KEY, JSON.stringify(setlist)); } catch { /* quota */ }
  }, [SETLIST_KEY, setlist]);
  useEffect(() => {
    try { localStorage.setItem(SETLIST_LOCK_KEY, setlistLock ? "1" : "0"); } catch { /* quota */ }
  }, [SETLIST_LOCK_KEY, setlistLock]);

  const enqueueTrack = (t) => {
    if (!t) return;
    setSetlist((q) => {
      // De-dupe by id so a double-click on the + button doesn't stack
      // the same track twice in the queue.
      if (q.some((x) => x.id === t.id)) {
        toast.info("Already in the queue");
        return q;
      }
      const next = [...q, t];
      toast.success(`Queued: ${t.title || t.name}`, { id: "setlist-add", duration: 2000 });
      return next;
    });
  };

  const dequeueTrack = (id) => setSetlist((q) => q.filter((x) => x.id !== id));
  const clearSetlist = () => setSetlist([]);
  const moveSetlistItem = (id, dir) => {
    setSetlist((q) => {
      const i = q.findIndex((x) => x.id === id);
      if (i < 0) return q;
      const j = i + dir;
      if (j < 0 || j >= q.length) return q;
      const next = q.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  // Auto-advance after sweep — loads the next queued track onto the
  // deck that just faded out. Deferred so the audio element has a clean
  // tick to remount with the new source.
  useEffect(() => {
    if (!setlistLock || sweepCompletedAt === 0) return;
    if (setlist.length === 0) return;
    const fromSide = sweepFromRef?.current;
    if (!fromSide) return;
    const nextTrack = setlist[0];
    setSetlist((q) => q.slice(1));
    setTimeout(() => {
      if (fromSide === "A") {
        smartLoad(nextTrack, setDeckA, setPlayA);
      } else {
        smartLoad(nextTrack, setDeckB, setPlayB);
      }
      toast.info(
        `SetList → Deck ${fromSide}: ${nextTrack.title || nextTrack.name}`,
        { id: "setlist-advance", duration: 2200 },
      );
    }, 200);
    if (sweepFromRef) sweepFromRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sweepCompletedAt]);

  // Auto-hydrate empty decks the moment SetList Lock is armed — saves
  // the host the manual "load A, load B, hit play" ritual when kicking
  // off an unattended set.
  useEffect(() => {
    if (!setlistLock) return;
    if (setlist.length === 0) return;
    if (!deckA && setlist.length >= 1) {
      const [first, ...rest] = setlist;
      setSetlist(rest);
      smartLoad(first, setDeckA, setPlayA);
      setTimeout(() => setPlayA(true), 600);
      return;
    }
    if (!deckB && setlist.length >= 1) {
      const [first, ...rest] = setlist;
      setSetlist(rest);
      smartLoad(first, setDeckB, setPlayB);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setlistLock, deckA, deckB, setlist.length]);

  return {
    setlist,
    setlistLock,
    setSetlistLock,
    enqueueTrack,
    dequeueTrack,
    clearSetlist,
    moveSetlistItem,
  };
}
